import React, { useCallback, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import VelavuNotice from "../../../elements/velavu-notice/velavu-notice";
import { MappedinCredentialsError, VelavuAPI } from "velavu-js-api";
import VelavuButton from "../../../elements/velavu-button";
import VelavuInput from "../../../elements/velavu-input";
import * as styles from "./change-mappedin-credentials-modal.module.scss";
import VelavuAlertModal from "../../../elements/velavu-alert-modal/velavu-alert-modal";
import VelavuAlertModalTitle from "../../../elements/velavu-alert-modal/velavu-alert-modal-title";
import VelavuAlertModalContent from "../../../elements/velavu-alert-modal/velavu-alert-modal-content";
import VelavuAlertModalActions from "../../../elements/velavu-alert-modal/velavu-alert-modal-actions";
import {
	CredentialValidationError,
	credentialValidationErrorToMessage,
} from "./mappedin-credential-validation";

export interface ChangeMappedinCredentialsModalProps {
	onClose: (updated: boolean) => void;
}

export default function ChangeMappedinCredentialsModal(
	props: ChangeMappedinCredentialsModalProps,
) {
	const { t } = useTranslation();
	const [apiKeyInput, setAPIKeyInput] = useState("");
	const [apiSecretInput, setAPISecretInput] = useState("");

	const sanitizedAPIKeyInput = useMemo(() => {
		return apiKeyInput.trim();
	}, [apiKeyInput]);
	const sanitizedSecretInput = useMemo(() => {
		return apiSecretInput.trim();
	}, [apiSecretInput]);
	const inputValid =
		sanitizedAPIKeyInput !== "" && sanitizedSecretInput !== "";

	const [isLoading, setIsLoading] = useState(false);
	const [error, setError] = useState<CredentialValidationError | null>(null);

	const save = useCallback(async () => {
		//Ignore if the input is invalid
		if (!inputValid || isLoading) {
			return;
		}

		//Reset the state
		setError(null);
		setIsLoading(true);

		//Update the credentials on the backend (using addIntegration which will replace existing credentials)
		try {
			await VelavuAPI.integrations.mappedin.addIntegration({
				api_key: sanitizedAPIKeyInput,
				secret: sanitizedSecretInput,
			});
			props.onClose(true);
		} catch (err) {
			if (err instanceof MappedinCredentialsError) {
				setError(CredentialValidationError.InvalidCredentials);
			} else {
				setError(CredentialValidationError.ExternalError);
			}
		} finally {
			setIsLoading(false);
		}
	}, [
		inputValid,
		isLoading,
		sanitizedAPIKeyInput,
		sanitizedSecretInput,
		props,
	]);

	return (
		<VelavuAlertModal>
			<VelavuAlertModalTitle>
				{t("settings.integrations.change_credentials_title")}
			</VelavuAlertModalTitle>

			<VelavuAlertModalContent>
				<VelavuInput
					titleText={t("settings.integrations.api_key")}
					placeholder={t("settings.integrations.api_key_placeholder")}
					value={apiKeyInput}
					onChange={setAPIKeyInput}
					disabled={isLoading}
					fullWidth
					bottomPadding
				/>

				<VelavuInput
					titleText={t("settings.integrations.secret")}
					placeholder={t("settings.integrations.secret_placeholder")}
					value={apiSecretInput}
					onChange={setAPISecretInput}
					disabled={isLoading}
					fullWidth
					bottomPadding
				/>

				{error !== null && (
					<VelavuNotice type="error" className={styles.statusNotice}>
						{credentialValidationErrorToMessage(error, t)}
					</VelavuNotice>
				)}
			</VelavuAlertModalContent>

			<VelavuAlertModalActions>
				<VelavuButton
					label={t("common.cancel")}
					variant="outlined"
					fullWidth
					onClick={() => props.onClose(false)}
					disabled={isLoading}
				/>

				<VelavuButton
					label={t("common.save")}
					variant="normal"
					fullWidth
					onClick={save}
					disabled={!inputValid || isLoading}
					loadingIndicator={isLoading}
				/>
			</VelavuAlertModalActions>
		</VelavuAlertModal>
	);
}
